import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties, MouseEvent, MutableRefObject } from 'react';
import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import { defaultRangeExtractor, useVirtualizer } from '@tanstack/react-virtual';
import type { Range } from '@tanstack/react-virtual';
import { api } from '../lib/api';
import type {
  PublicAccount,
  SearchResponse,
  ThreadCategory,
  ThreadDetail,
  ThreadView,
  ThreadsPage,
} from '../lib/api';
import { useStore } from '../lib/store';
import type { Density, RowLines } from '../lib/store';
import { ThreadRow, rowHeightFor } from './ThreadRow';
import styles from './ThreadList.module.css';

const PAGE_SIZE = 50;
const UNDO_WINDOW_MS = 10_000;
const SKELETON_DELAY_MS = 120;
const HEADER_HEIGHT = 28;
/** Below this the pane cannot hold two comfortable lines; drop one density step. */
const NARROW_PANE_PX = 340;
/**
 * One-line rows only work when the column can actually hold sender + subject +
 * preview + timestamp side by side. The main column is now the whole window
 * minus a 40px rail, so this threshold is only ever crossed on a phone — where
 * two lines are strictly more scannable than two ellipses side by side.
 */
const ONE_LINE_MIN_PX = 560;
const DENSITY_ORDER: Density[] = ['compact', 'cozy', 'comfortable'];
/** 2px padding either side of a 26px button — see .cluster in the stylesheet. */
const CLUSTER_H = 30;

const CATEGORY_ORDER: readonly ThreadCategory[] = [
  'priority',
  'people',
  'newsletters',
  'notifications',
  'receipts',
];
const CATEGORY_LABEL: Record<string, string> = {
  priority: 'Priority',
  people: 'People',
  newsletters: 'Newsletters',
  notifications: 'Notifications',
  receipts: 'Receipts',
  other: 'Everything else',
};

type ThreadsInfinite = { pages: ThreadsPage[]; pageParams: unknown[] };

type ListItem =
  | { kind: 'header'; key: string; label: string; count: number | null }
  | { kind: 'thread'; key: string; thread: ThreadView; index: number };

type UndoToast = { id: number; label: string; onUndo: () => void };

export interface ThreadListProps {
  /** Server-side category filter; independent of `groupByCategory`. */
  category?: ThreadCategory | null;
  /** When set the list shows search results (unpaginated) instead of the view. */
  searchQuery?: string | null;
  /** Split the list into sticky category sections. Wins over date grouping. */
  groupByCategory?: boolean;
  /** Break the list with sticky date headers ("Earlier this month"). */
  groupByDate?: boolean;
  /** Overrides the store density, e.g. for a preview surface. */
  density?: Density;
  /** Set false when another surface owns j/k (search overlay, settings). */
  keyboardEnabled?: boolean;
  /**
   * While the reading view is up, j/k must not merely move the cursor — it has
   * to load the next conversation into the pane the user is already reading.
   */
  openOnMove?: boolean;
  /** Filled with the scroll container, so the shell can save and restore it. */
  scrollElementRef?: MutableRefObject<HTMLElement | null>;
  className?: string;
  onOpenThread?: (threadKey: string) => void;
}

function cssVars(vars: Record<string, string | number>): CSSProperties {
  return vars as unknown as CSSProperties;
}

/** Inclusive key range between two list positions, in either direction. */
function rangeKeys(threads: ThreadView[], from: number, to: number): Set<string> {
  const lo = Math.max(0, Math.min(from, to));
  const hi = Math.min(threads.length - 1, Math.max(from, to));
  const keys = new Set<string>();
  for (let i = lo; i <= hi; i += 1) {
    const thread = threads[i];
    if (thread) keys.add(thread.threadKey);
  }
  return keys;
}

/* --- date grouping --------------------------------------------------------
   Superhuman breaks the list with date headers rather than drawing rules
   between rows. The threads arrive newest-first, so the buckets can be emitted
   in encounter order and never need sorting. */

const monthFmt = new Intl.DateTimeFormat(undefined, { month: 'long' });
const monthYearFmt = new Intl.DateTimeFormat(undefined, { month: 'long', year: 'numeric' });

function startOfDay(d: Date): number {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

function dateGroup(epochSeconds: number, now: number): { key: string; label: string } {
  const ms = epochSeconds * 1000;
  if (!Number.isFinite(ms)) return { key: 'unknown', label: 'Undated' };
  const then = new Date(ms);
  const today = new Date(now);
  const days = Math.round((startOfDay(today) - startOfDay(then)) / 86_400_000);

  if (days <= 0) return { key: 'today', label: 'Today' };
  if (days === 1) return { key: 'yesterday', label: 'Yesterday' };
  if (days < 7) return { key: 'week', label: 'Earlier this week' };
  if (then.getFullYear() === today.getFullYear() && then.getMonth() === today.getMonth()) {
    return { key: 'month', label: 'Earlier this month' };
  }
  const key = `${then.getFullYear()}-${then.getMonth()}`;
  const label =
    then.getFullYear() === today.getFullYear() ? monthFmt.format(then) : monthYearFmt.format(then);
  return { key, label };
}

function ArchiveIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path
        d="M2 4.2h12M3.4 4.2v8.2h9.2V4.2M6.3 7.6 8 9.4l1.7-1.8"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ClockIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <circle cx="8" cy="8" r="5.8" stroke="currentColor" strokeWidth="1.2" />
      <path d="M8 4.8V8l2.2 1.4" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path
        d="M2.8 4.2h10.4M6.2 4.2V2.8h3.6v1.4M4.2 4.2l.6 9h6.4l.6-9"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function UnreadIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <rect x="2.2" y="3.6" width="11.6" height="8.8" rx="1.6" stroke="currentColor" strokeWidth="1.2" />
      <path d="m2.6 5 5.4 3.6L13.4 5" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" />
    </svg>
  );
}

function StarGlyph({ filled }: { filled: boolean }) {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
      <path
        d="M8 1.8l1.86 3.77 4.16.6-3.01 2.93.71 4.14L8 11.29l-3.72 1.95.71-4.14L1.98 6.17l4.16-.6z"
        fill={filled ? 'currentColor' : 'none'}
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** The hint under an empty list always names a key, never just a noun. */
type EmptyCopy = { title: string; body: string; keys: [string, string][] };

function emptyCopy(view: string, search: string | null): EmptyCopy {
  if (search) {
    return {
      title: 'No matches',
      body: `Nothing matched “${search}”. Try fewer words, or a sender.`,
      keys: [['/', 'search again']],
    };
  }
  switch (view) {
    case 'unread':
      return {
        title: 'All caught up',
        body: 'Nothing unread across your mailboxes.',
        keys: [['g i', 'inbox']],
      };
    case 'starred':
      return {
        title: 'Nothing starred',
        body: 'Star a thread to keep it within reach.',
        keys: [['s', 'star']],
      };
    case 'sent':
      return { title: 'Nothing sent yet', body: 'Messages you send appear here.', keys: [['c', 'compose']] };
    case 'archive':
      return {
        title: 'Archive is empty',
        body: 'Archived threads stay searchable forever.',
        keys: [['/', 'search']],
      };
    case 'trash':
      return {
        title: 'Trash is empty',
        body: 'Deleted threads land here before they are gone.',
        keys: [['g i', 'inbox']],
      };
    case 'snoozed':
      return {
        title: 'Nothing snoozed',
        body: 'Snoozed threads return to the inbox on schedule.',
        keys: [['h', 'snooze']],
      };
    default:
      return {
        title: 'Inbox zero',
        body: 'Nothing left to triage. Archived mail is still one search away.',
        keys: [
          ['c', 'compose'],
          ['/', 'search'],
        ],
      };
  }
}

/** Renders 'g i' as two chips, matching the cheat sheet's chord notation. */
function Keys({ chord }: { chord: string }) {
  return (
    <>
      {chord.split(' ').map((step, i) => (
        <kbd key={`${step}-${i}`} className="kiln-kbd">
          {step}
        </kbd>
      ))}
    </>
  );
}

export function ThreadList({
  category = null,
  searchQuery = null,
  groupByCategory = false,
  groupByDate = true,
  density: densityOverride,
  keyboardEnabled = true,
  openOnMove = false,
  scrollElementRef,
  className,
  onOpenThread,
}: ThreadListProps) {
  const qc = useQueryClient();
  const view = useStore((s) => s.view);
  const accountFilter = useStore((s) => s.accountFilter);
  const storeDensity = useStore((s) => s.density);
  const appearance = useStore((s) => s.appearance);
  const setListEmpty = useStore((s) => s.setListEmpty);
  const selectedThreadKey = useStore((s) => s.selectedThreadKey);
  const setSelectedThreadKey = useStore((s) => s.setSelectedThreadKey);
  const paletteOpen = useStore((s) => s.paletteOpen);

  const accountId = typeof accountFilter === 'number' ? accountFilter : undefined;

  const paneRef = useRef<HTMLDivElement | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const [narrow, setNarrow] = useState(false);
  const [stacked, setStacked] = useState(false);
  const [focusKey, setFocusKey] = useState<string | null>(null);
  const [checked, setChecked] = useState<ReadonlySet<string>>(() => new Set());
  const [hoverKey, setHoverKey] = useState<string | null>(null);
  const [toast, setToast] = useState<UndoToast | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const [showSkeleton, setShowSkeleton] = useState(false);

  const anchorIndexRef = useRef<number | null>(null);
  const lastFocusIndexRef = useRef(0);
  const pendingTrashRef = useRef(new Map<string, ReturnType<typeof setTimeout>>());
  const toastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // The shell saves and restores the scroll offset around search mode, and it
  // cannot reach into this component for the scroller any other way.
  useLayoutEffect(() => {
    if (!scrollElementRef) return;
    scrollElementRef.current = scrollRef.current;
    return () => {
      scrollElementRef.current = null;
    };
  }, [scrollElementRef]);

  /* --- data ------------------------------------------------------------- */

  const params = useMemo(
    () => ({ view, account: accountId, category: category ?? undefined }),
    [view, accountId, category],
  );

  const listQuery = useInfiniteQuery<ThreadsPage>({
    queryKey: ['threads', params],
    initialPageParam: null as number | null,
    queryFn: ({ pageParam }) =>
      api.threads({ ...params, limit: PAGE_SIZE, before: (pageParam as number | null) ?? undefined }),
    getNextPageParam: (last) => last.nextBefore ?? null,
    enabled: !searchQuery,
  });

  const searchResults = useQuery({
    queryKey: ['search', searchQuery, accountId],
    queryFn: () => api.search(searchQuery as string, accountId != null ? { account: accountId } : undefined),
    enabled: Boolean(searchQuery),
  });

  const accountsQuery = useQuery({ queryKey: ['accounts'], queryFn: () => api.accounts() });
  const accounts: PublicAccount[] = accountsQuery.data?.accounts ?? [];

  const colorByAccount = useMemo(() => {
    const map = new Map<number, string>();
    for (const a of accounts) map.set(a.id, a.color);
    return map;
  }, [accounts]);

  const selfAddresses = useMemo(
    () =>
      accounts.flatMap((a) =>
        [a.email, a.sendAs].filter((v): v is string => Boolean(v)).map((v) => v.toLowerCase()),
      ),
    [accounts],
  );

  const threads = useMemo(() => {
    const source = searchQuery
      ? (searchResults.data?.threads ?? [])
      : (listQuery.data?.pages ?? []).flatMap((p) => p.threads);
    // Pages can overlap when new mail lands between requests.
    const seen = new Set<string>();
    const out: ThreadView[] = [];
    for (const t of source) {
      if (seen.has(t.threadKey)) continue;
      seen.add(t.threadKey);
      out.push(t);
    }
    return out;
  }, [searchQuery, searchResults.data, listQuery.data]);

  const isPending = searchQuery ? searchResults.isPending : listQuery.isPending;
  const isFetching = searchQuery ? searchResults.isFetching : listQuery.isFetching;
  const error = searchQuery ? searchResults.error : listQuery.error;

  /* --- density ---------------------------------------------------------- */

  useEffect(() => {
    const el = paneRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      setNarrow(entry.contentRect.width < NARROW_PANE_PX);
      setStacked(entry.contentRect.width < ONE_LINE_MIN_PX);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const requested = densityOverride ?? storeDensity;
  const density: Density = narrow
    ? (DENSITY_ORDER[Math.max(0, DENSITY_ORDER.indexOf(requested) - 1)] ?? requested)
    : requested;
  const lines: RowLines = stacked ? 2 : appearance.rowLines;
  const rowHeight = rowHeightFor(density, lines);

  /* --- items ------------------------------------------------------------ */

  const items = useMemo<ListItem[]>(() => {
    if (groupByCategory) {
      const buckets = new Map<string, { thread: ThreadView; index: number }[]>();
      threads.forEach((thread, index) => {
        const bucket = thread.category && CATEGORY_LABEL[thread.category] ? thread.category : 'other';
        const list = buckets.get(bucket);
        if (list) list.push({ thread, index });
        else buckets.set(bucket, [{ thread, index }]);
      });

      const order = [...CATEGORY_ORDER, 'other'].filter((c) => buckets.has(c));
      const out: ListItem[] = [];
      for (const bucket of order) {
        const entries = buckets.get(bucket) ?? [];
        out.push({
          kind: 'header',
          key: `header:${bucket}`,
          label: CATEGORY_LABEL[bucket] ?? bucket,
          count: entries.length,
        });
        for (const { thread, index } of entries) {
          out.push({ kind: 'thread', key: thread.threadKey, thread, index });
        }
      }
      return out;
    }

    if (!groupByDate) {
      return threads.map((thread, index) => ({
        kind: 'thread',
        key: thread.threadKey,
        thread,
        index,
      }));
    }

    // Search results are ranked by relevance, not recency, so date headers over
    // them would claim an ordering the list does not have.
    if (searchQuery) {
      return threads.map((thread, index) => ({
        kind: 'thread',
        key: thread.threadKey,
        thread,
        index,
      }));
    }

    const out: ListItem[] = [];
    let currentKey: string | null = null;
    threads.forEach((thread, index) => {
      const group = dateGroup(thread.lastDate, now);
      if (group.key !== currentKey) {
        currentKey = group.key;
        out.push({ kind: 'header', key: `header:${group.key}`, label: group.label, count: null });
      }
      out.push({ kind: 'thread', key: thread.threadKey, thread, index });
    });
    return out;
  }, [threads, groupByCategory, groupByDate, searchQuery, now]);

  const grouped = groupByCategory || (groupByDate && !searchQuery);

  const itemIndexByThreadIndex = useMemo(() => {
    const map: number[] = [];
    items.forEach((item, i) => {
      if (item.kind === 'thread') map[item.index] = i;
    });
    return map;
  }, [items]);

  const headerIndexes = useMemo(
    () => items.reduce<number[]>((acc, item, i) => (item.kind === 'header' ? [...acc, i] : acc), []),
    [items],
  );

  /* --- virtualizer ------------------------------------------------------ */

  const activeStickyRef = useRef(-1);

  const rangeExtractor = useCallback(
    (range: Range) => {
      let sticky = -1;
      for (const i of headerIndexes) {
        if (i <= range.startIndex) sticky = i;
        else break;
      }
      activeStickyRef.current = sticky;
      const indexes = new Set(defaultRangeExtractor(range));
      if (sticky >= 0) indexes.add(sticky);
      return [...indexes].sort((a, b) => a - b);
    },
    [headerIndexes],
  );

  const virtualizer = useVirtualizer({
    count: items.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: (i) => (items[i]?.kind === 'header' ? HEADER_HEIGHT : rowHeight),
    getItemKey: (i) => items[i]?.key ?? i,
    overscan: 8,
    rangeExtractor: grouped ? rangeExtractor : undefined,
  });

  const virtualItems = virtualizer.getVirtualItems();

  useEffect(() => {
    virtualizer.measure();
  }, [rowHeight, virtualizer]);

  /* --- pagination ------------------------------------------------------- */

  const { hasNextPage, isFetchingNextPage, fetchNextPage } = listQuery;

  const lastVisibleIndex = virtualItems[virtualItems.length - 1]?.index ?? -1;

  useEffect(() => {
    if (searchQuery || lastVisibleIndex < 0) return;
    if (lastVisibleIndex >= items.length - 6 && hasNextPage && !isFetchingNextPage) {
      void fetchNextPage();
    }
  }, [lastVisibleIndex, items.length, hasNextPage, isFetchingNextPage, fetchNextPage, searchQuery]);

  /* --- clocks ----------------------------------------------------------- */

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (!isPending || threads.length > 0) {
      setShowSkeleton(false);
      return;
    }
    // A skeleton that flashes for 40ms reads as a bug, not as speed.
    const id = setTimeout(() => setShowSkeleton(true), SKELETON_DELAY_MS);
    return () => clearTimeout(id);
  }, [isPending, threads.length]);

  /* --- cache mutation helpers ------------------------------------------ */

  const patchThreads = useCallback(
    (keys: ReadonlySet<string>, fn: (t: ThreadView) => ThreadView | null) => {
      qc.setQueriesData<ThreadsInfinite>({ queryKey: ['threads'] }, (old) => {
        // setQueriesData matches every query under the ['threads'] prefix, not
        // just this infinite one. Anything else stored there (a plain page, a
        // count) has no `pages` array, and mapping over it throws inside the
        // updater — which surfaces as a blank pane rather than a caught error.
        if (!old || !Array.isArray(old.pages)) return old;
        return {
          ...old,
          pages: old.pages.map((page) => {
            const next: ThreadView[] = [];
            let changed = false;
            for (const t of page.threads) {
              if (!keys.has(t.threadKey)) {
                next.push(t);
                continue;
              }
              changed = true;
              const patched = fn(t);
              if (patched) next.push(patched);
            }
            return changed ? { ...page, threads: next } : page;
          }),
        };
      });

      qc.setQueriesData<SearchResponse>({ queryKey: ['search'] }, (old) => {
        if (!old || !Array.isArray(old.threads)) return old;
        const next: ThreadView[] = [];
        for (const t of old.threads) {
          if (!keys.has(t.threadKey)) {
            next.push(t);
            continue;
          }
          const patched = fn(t);
          if (patched) next.push(patched);
        }
        return { ...old, threads: next };
      });
    },
    [qc],
  );

  const invalidateThreads = useCallback(() => {
    void qc.invalidateQueries({ queryKey: ['threads'] });
    void qc.invalidateQueries({ queryKey: ['search'] });
  }, [qc]);

  const showToast = useCallback((label: string, onUndo: () => void) => {
    setToast({ id: Date.now(), label, onUndo });
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(() => setToast(null), UNDO_WINDOW_MS);
  }, []);

  const dismissToast = useCallback(() => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    toastTimerRef.current = null;
    setToast(null);
  }, []);

  /**
   * Optimistic by construction: the cache is patched first, the network call is
   * fire-and-forget, and a rejection restores the exact pre-action snapshot.
   */
  const runAction = useCallback(
    async (
      keys: string[],
      apply: (t: ThreadView) => ThreadView | null,
      call: (key: string) => Promise<unknown>,
    ) => {
      if (keys.length === 0) return;
      const snapshot = qc.getQueriesData<ThreadsInfinite>({ queryKey: ['threads'] });
      patchThreads(new Set(keys), apply);
      const results = await Promise.allSettled(keys.map(call));
      if (results.some((r) => r.status === 'rejected')) {
        for (const [key, data] of snapshot) qc.setQueryData(key, data);
      }
      invalidateThreads();
    },
    [qc, patchThreads, invalidateThreads],
  );

  const setRead = useCallback(
    (keys: string[], value: boolean) => {
      void runAction(
        keys,
        (t) => ({ ...t, unreadCount: value ? 0 : Math.max(1, t.unreadCount) }),
        (key) => api.threadAction(key, 'read', { value }),
      );
      // Patch the open thread rather than invalidating it: refetching the pane
      // the user is reading is visible work for a value we already know.
      for (const key of keys) {
        qc.setQueryData<ThreadDetail>(['thread', key], (old) =>
          old
            ? {
                thread: { ...old.thread, unreadCount: value ? 0 : Math.max(1, old.thread.unreadCount) },
                messages: old.messages.map((m) => ({ ...m, seen: value })),
              }
            : old,
        );
      }
    },
    [runAction, qc],
  );

  const setStar = useCallback(
    (key: string, value: boolean) => {
      void runAction(
        [key],
        (t) => ({ ...t, flagged: value }),
        (k) => api.threadAction(k, 'star', { value }),
      );
    },
    [runAction],
  );

  const archive = useCallback(
    (keys: string[]) => {
      if (keys.length === 0) return;
      void runAction(
        keys,
        () => null,
        (key) => api.threadAction(key, 'archive'),
      );
      showToast(keys.length === 1 ? 'Archived' : `Archived ${keys.length} threads`, () => {
        dismissToast();
        void runAction(
          keys,
          (t) => t,
          (key) => api.threadAction(key, 'archive', { restore: true }),
        );
      });
    },
    [runAction, showToast, dismissToast],
  );

  const snooze = useCallback(
    (keys: string[]) => {
      void runAction(
        keys,
        () => null,
        (key) =>
          api.threadAction(key, 'snooze', {
            until: Math.floor(Date.now() / 1000) + 60 * 60 * 24,
          }),
      );
    },
    [runAction],
  );

  /**
   * Trash has no server-side inverse, so the commit itself is what waits out the
   * undo window. Undo cancels the timer; nothing ever reaches IMAP.
   */
  const commitTrash = useCallback(
    (key: string) => {
      pendingTrashRef.current.delete(key);
      void api.threadAction(key, 'trash').then(
        () => invalidateThreads(),
        () => invalidateThreads(),
      );
    },
    [invalidateThreads],
  );

  const trash = useCallback(
    (keys: string[]) => {
      if (keys.length === 0) return;
      patchThreads(new Set(keys), () => null);
      for (const key of keys) {
        const timer = setTimeout(() => commitTrash(key), UNDO_WINDOW_MS);
        pendingTrashRef.current.set(key, timer);
      }
      showToast(keys.length === 1 ? 'Moved to Trash' : `Moved ${keys.length} threads to Trash`, () => {
        dismissToast();
        for (const key of keys) {
          const timer = pendingTrashRef.current.get(key);
          if (timer) clearTimeout(timer);
          pendingTrashRef.current.delete(key);
        }
        invalidateThreads();
      });
    },
    [patchThreads, commitTrash, showToast, dismissToast, invalidateThreads],
  );

  useEffect(() => {
    const pending = pendingTrashRef.current;
    const flush = () => {
      for (const [key, timer] of pending) {
        clearTimeout(timer);
        void api.threadAction(key, 'trash');
      }
      pending.clear();
    };
    window.addEventListener('beforeunload', flush);
    return () => {
      window.removeEventListener('beforeunload', flush);
      flush();
    };
  }, []);

  /* --- selection -------------------------------------------------------- */

  const focusIndex = useMemo(() => {
    if (!focusKey) return -1;
    return threads.findIndex((t) => t.threadKey === focusKey);
  }, [focusKey, threads]);

  const scrollToThread = useCallback(
    (index: number) => {
      const itemIndex = itemIndexByThreadIndex[index];
      if (itemIndex == null) return;
      virtualizer.scrollToIndex(itemIndex, { align: 'auto' });
    },
    [itemIndexByThreadIndex, virtualizer],
  );

  /** Returns the thread the cursor landed on, so j/k can also open it. */
  const moveFocus = useCallback(
    (delta: number, extend: boolean): string | null => {
      if (threads.length === 0) return null;
      const from = focusIndex >= 0 ? focusIndex : delta > 0 ? -1 : threads.length;
      const next = Math.min(threads.length - 1, Math.max(0, from + delta));
      const thread = threads[next];
      if (!thread) return null;
      setFocusKey(thread.threadKey);
      scrollToThread(next);

      if (!extend) {
        anchorIndexRef.current = next;
        return thread.threadKey;
      }
      if (anchorIndexRef.current == null) anchorIndexRef.current = from >= 0 ? from : next;
      const anchor = anchorIndexRef.current;
      setChecked(rangeKeys(threads, anchor, next));
      return null;
    },
    [threads, focusIndex, scrollToThread],
  );

  const openThread = useCallback(
    (key: string) => {
      setFocusKey(key);
      anchorIndexRef.current = threads.findIndex((t) => t.threadKey === key);
      setSelectedThreadKey(key);
      onOpenThread?.(key);
      const thread = threads.find((t) => t.threadKey === key);
      if (thread && thread.unreadCount > 0) setRead([key], true);
    },
    [threads, setSelectedThreadKey, onOpenThread, setRead],
  );

  const toggleChecked = useCallback((key: string) => {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const handleRowClick = useCallback(
    (key: string, event: MouseEvent<HTMLElement>) => {
      const index = threads.findIndex((t) => t.threadKey === key);
      if (index < 0) return;

      if (event.shiftKey) {
        // The anchor lives in list coordinates, so it survives being scrolled
        // out of the window — the DOM is not the source of truth here.
        setChecked(rangeKeys(threads, anchorIndexRef.current ?? index, index));
        setFocusKey(key);
        event.preventDefault();
        return;
      }

      if (event.metaKey || event.ctrlKey) {
        toggleChecked(key);
        setFocusKey(key);
        anchorIndexRef.current = index;
        return;
      }

      setChecked(new Set());
      openThread(key);
    },
    [threads, toggleChecked, openThread],
  );

  const actionTargets = useCallback(
    (key?: string | null): string[] => {
      if (checked.size > 0) return [...checked];
      const target = key ?? focusKey;
      return target ? [target] : [];
    },
    [checked, focusKey],
  );

  const clearSelection = useCallback(() => setChecked(new Set()), []);

  /* --- keyboard --------------------------------------------------------- */

  useEffect(() => {
    if (!keyboardEnabled || paletteOpen) return;

    const handler = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.altKey || e.metaKey || e.ctrlKey) return;
      const target = e.target as HTMLElement | null;
      if (target) {
        const tag = target.tagName;
        if (
          tag === 'INPUT' ||
          tag === 'TEXTAREA' ||
          tag === 'SELECT' ||
          target.isContentEditable ||
          target.closest('[role="dialog"]')
        ) {
          return;
        }
      }

      switch (e.key) {
        case 'j':
        case 'J': {
          const landed = moveFocus(1, e.shiftKey);
          // While reading, the cursor and the open conversation are the same
          // thing: j loads the next one into the pane rather than leaving the
          // reader looking at a thread the cursor has already left.
          if (landed && openOnMove) openThread(landed);
          break;
        }
        case 'k':
        case 'K': {
          const landed = moveFocus(-1, e.shiftKey);
          if (landed && openOnMove) openThread(landed);
          break;
        }
        case 'Enter':
        case 'o':
          if (focusKey) openThread(focusKey);
          break;
        case 'x': {
          if (!focusKey) return;
          toggleChecked(focusKey);
          anchorIndexRef.current = focusIndex >= 0 ? focusIndex : null;
          break;
        }
        case 'e': {
          const targets = actionTargets();
          if (targets.length === 0) return;
          archive(targets);
          clearSelection();
          break;
        }
        case '#': {
          const targets = actionTargets();
          if (targets.length === 0) return;
          trash(targets);
          clearSelection();
          break;
        }
        case 's': {
          const target = threads.find((t) => t.threadKey === focusKey);
          if (!target) return;
          setStar(target.threadKey, !target.flagged);
          break;
        }
        case 'u': {
          const targets = actionTargets();
          if (targets.length === 0) return;
          setRead(targets, false);
          clearSelection();
          break;
        }
        case 'Escape':
          if (checked.size > 0) clearSelection();
          else return;
          break;
        default:
          return;
      }
      e.preventDefault();
    };

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [
    keyboardEnabled,
    paletteOpen,
    moveFocus,
    openOnMove,
    focusKey,
    focusIndex,
    openThread,
    toggleChecked,
    actionTargets,
    archive,
    trash,
    setStar,
    setRead,
    clearSelection,
    checked.size,
    threads,
  ]);

  useEffect(() => {
    if (focusIndex >= 0) lastFocusIndexRef.current = focusIndex;
  }, [focusIndex]);

  // Archiving the focused row must leave the cursor where the row was, not
  // throw it back to the top of the list.
  useLayoutEffect(() => {
    if (focusKey && threads.some((t) => t.threadKey === focusKey)) return;
    if (threads.length === 0) {
      setFocusKey(null);
      anchorIndexRef.current = null;
      return;
    }
    const index = Math.max(0, Math.min(lastFocusIndexRef.current, threads.length - 1));
    setFocusKey(threads[index]?.threadKey ?? null);
    anchorIndexRef.current = index;
  }, [threads, focusKey]);

  useEffect(() => {
    setChecked(new Set());
    setFocusKey(null);
    anchorIndexRef.current = null;
    scrollRef.current?.scrollTo({ top: 0 });
  }, [view, accountId, category, searchQuery]);

  // The reading view owns the full Inbox Zero moment, but only this component
  // knows the list ran out of threads. Published rather than lifted so the two
  // surfaces stay independently mountable.
  //
  // Restricted to the unfiltered inbox on purpose: an empty Receipts split is
  // not inbox zero, and announcing it as such would cheapen the real thing.
  const empty =
    !isPending &&
    !error &&
    threads.length === 0 &&
    !searchQuery &&
    view === 'inbox' &&
    category === null;
  useEffect(() => {
    setListEmpty(empty);
    return () => setListEmpty(false);
  }, [empty, setListEmpty]);

  /* --- hover cluster ---------------------------------------------------- */

  const hoverItemIndex = useMemo(() => {
    if (!hoverKey || checked.size > 0) return -1;
    return items.findIndex((i) => i.kind === 'thread' && i.thread.threadKey === hoverKey);
  }, [hoverKey, items, checked.size]);

  const hoverOffset = useMemo(() => {
    if (hoverItemIndex < 0) return null;
    const match = virtualItems.find((v) => v.index === hoverItemIndex);
    return match ? match.start : null;
  }, [hoverItemIndex, virtualItems]);

  const hoverThread = hoverKey ? threads.find((t) => t.threadKey === hoverKey) : undefined;
  // The cluster covers the timestamp, so it has to paint whatever fill the row
  // beneath it is painting — otherwise the old time bleeds through the icons.
  const hoverTone =
    hoverThread && (selectedThreadKey === hoverThread.threadKey || focusKey === hoverThread.threadKey)
      ? 'selected'
      : 'hover';

  /* --- render ----------------------------------------------------------- */

  const copy = emptyCopy(view, searchQuery);
  const showEmpty = !isPending && !error && threads.length === 0;

  return (
    <div ref={paneRef} className={`${styles.pane} ${className ?? ''}`}>
      {isFetching && threads.length > 0 && <div className={styles.progress} aria-hidden="true" />}

      <div
        ref={scrollRef}
        className={styles.scroll}
        id="kiln-thread-list"
        tabIndex={-1}
        role="listbox"
        aria-label="Threads"
        aria-multiselectable
        onMouseLeave={() => setHoverKey(null)}
      >
        {showSkeleton && (
          <div className={styles.skeleton} aria-hidden="true">
            {Array.from({ length: 10 }, (_, i) => (
              <div
                key={i}
                className={styles.skelRow}
                data-lines={lines}
                style={{ height: rowHeight }}
              >
                <div className={styles.skelDot} />
                <div className={styles.skelLines}>
                  <div className={styles.skelLine} style={{ width: `${38 - i * 2}%` }} />
                  {lines === 2 && (
                    <div className={styles.skelLine} style={{ width: `${72 - i * 4}%` }} />
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {error && !showSkeleton && threads.length === 0 && (
          <div className={styles.empty}>
            <div className={styles.emptyInner}>
              <h2 className={styles.emptyTitle}>Could not load mail</h2>
              <p className={styles.emptyBody}>
                The server did not answer. Your mail is untouched — this is only the view.
              </p>
              <button
                type="button"
                className={styles.retry}
                onClick={() => {
                  if (searchQuery) void searchResults.refetch();
                  else void listQuery.refetch();
                }}
              >
                Try again
              </button>
            </div>
          </div>
        )}

        {showEmpty && (
          <div className={styles.empty}>
            <div className={styles.emptyInner}>
              <h2 className={styles.emptyTitle}>{copy.title}</h2>
              <p className={styles.emptyBody}>{copy.body}</p>
              <div className={styles.emptyKeys}>
                {copy.keys.map(([chord, label]) => (
                  <span key={chord} className="kiln-kbd-run">
                    <Keys chord={chord} />
                    <span className={styles.emptyKeyLabel}>{label}</span>
                  </span>
                ))}
              </div>
            </div>
          </div>
        )}

        {threads.length > 0 && (
          <div className={styles.inner} style={{ height: virtualizer.getTotalSize() }}>
            {virtualItems.map((virtualItem) => {
              const item = items[virtualItem.index];
              if (!item) return null;

              if (item.kind === 'header') {
                const isSticky = virtualItem.index === activeStickyRef.current;
                return (
                  <div
                    key={item.key}
                    className={`${styles.virtualItem} ${styles.header}`}
                    style={
                      isSticky
                        ? { position: 'sticky', top: 0, zIndex: 2, transform: 'none' }
                        : { height: virtualItem.size, transform: `translateY(${virtualItem.start}px)` }
                    }
                  >
                    <span>{item.label}</span>
                    {item.count !== null && <span className={styles.headerCount}>{item.count}</span>}
                  </div>
                );
              }

              const thread = item.thread;
              return (
                <div
                  key={item.key}
                  className={styles.virtualItem}
                  style={{ height: virtualItem.size, transform: `translateY(${virtualItem.start}px)` }}
                >
                  <ThreadRow
                    thread={thread}
                    density={density}
                    lines={lines}
                    // `appearance.showAvatars` is deliberately not read here.
                    // The measured row has no avatar at any density; the toggle
                    // survives only because AppearanceSettings owns it.
                    showSnippet={appearance.showSnippets}
                    accountColors={thread.accountIds.map(
                      (id) => colorByAccount.get(id) ?? 'var(--accent-9)',
                    )}
                    selfAddresses={selfAddresses}
                    focused={focusKey === thread.threadKey}
                    checked={checked.has(thread.threadKey)}
                    active={selectedThreadKey === thread.threadKey}
                    now={now}
                    onOpen={handleRowClick}
                    onPointerEnter={setHoverKey}
                    onToggleStar={setStar}
                  />
                </div>
              );
            })}

            {hoverOffset != null && hoverThread && (
              <div
                className={styles.cluster}
                data-tone={hoverTone}
                style={cssVars({ top: `${hoverOffset + (rowHeight - CLUSTER_H) / 2}px` })}
                onMouseLeave={() => setHoverKey(null)}
              >
                <button
                  type="button"
                  className={styles.clusterBtn}
                  title="Done — archive (e)"
                  aria-label="Archive"
                  onClick={() => archive([hoverThread.threadKey])}
                >
                  <ArchiveIcon />
                </button>
                <button
                  type="button"
                  className={styles.clusterBtn}
                  title="Remind me tomorrow (h)"
                  aria-label="Snooze until tomorrow"
                  onClick={() => snooze([hoverThread.threadKey])}
                >
                  <ClockIcon />
                </button>
                <button
                  type="button"
                  className={styles.clusterBtn}
                  title="Trash (#)"
                  aria-label="Move to trash"
                  onClick={() => trash([hoverThread.threadKey])}
                >
                  <TrashIcon />
                </button>
                <button
                  type="button"
                  className={styles.clusterBtn}
                  data-on={hoverThread.flagged}
                  title={hoverThread.flagged ? 'Unstar (s)' : 'Star (s)'}
                  aria-label={hoverThread.flagged ? 'Unstar thread' : 'Star thread'}
                  onClick={() => setStar(hoverThread.threadKey, !hoverThread.flagged)}
                >
                  <StarGlyph filled={hoverThread.flagged} />
                </button>
                <button
                  type="button"
                  className={styles.clusterBtn}
                  title="Mark unread (u)"
                  aria-label="Mark unread"
                  onClick={() => setRead([hoverThread.threadKey], hoverThread.unreadCount > 0)}
                >
                  <UnreadIcon />
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {toast && (
        <div className={styles.toast} role="status">
          <span className={styles.toastLabel}>{toast.label}</span>
          <button type="button" className={styles.toastAction} onClick={toast.onUndo}>
            Undo
          </button>
        </div>
      )}
    </div>
  );
}

export default ThreadList;
